/**
 * Utility functions for managing persistent invitation codes
 * Invitation codes are stored in localStorage with an expiry date
 */

const INVITATION_CODE_KEY = 'invitation_code';
const INVITATION_CODE_EXPIRY_KEY = 'invitation_code_expiry';
const EXPIRY_DAYS = 30; // Invitation code valid for 30 days

/**
 * Store an invitation code in localStorage with expiry
 */
export const storeInvitationCode = (code: string): void => {
  if (!code) return;
  
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + EXPIRY_DAYS);
  
  localStorage.setItem(INVITATION_CODE_KEY, code.toUpperCase());
  localStorage.setItem(INVITATION_CODE_EXPIRY_KEY, expiryDate.toISOString());
};

/**
 * Retrieve a valid invitation code from localStorage
 * Returns null if no code exists or if the code has expired
 */
export const getInvitationCode = (): string | null => {
  const code = localStorage.getItem(INVITATION_CODE_KEY);
  const expiryString = localStorage.getItem(INVITATION_CODE_EXPIRY_KEY);
  
  if (!code || !expiryString) {
    return null;
  }
  
  const expiryDate = new Date(expiryString);
  const now = new Date();
  
  if (expiryDate <= now) {
    // Code has expired, clean it up
    clearInvitationCode();
    return null;
  }
  
  return code;
};

/**
 * Clear the invitation code from localStorage
 */
export const clearInvitationCode = (): void => {
  localStorage.removeItem(INVITATION_CODE_KEY);
  localStorage.removeItem(INVITATION_CODE_EXPIRY_KEY);
};

/**
 * Extract invitation code from URL query parameters and store it
 * Should be called when the app loads or navigates to a new page
 */
export const captureInvitationCodeFromUrl = (searchParams: URLSearchParams): void => {
  const code = searchParams.get('ic');
  if (code) {
    storeInvitationCode(code);
  }
};
